import axios from "axios";
import { API_BASEURL } from "../utils/baseURL";
import { authHeader } from "../utils/headers";
import { TagsProps } from "../types/TagsProps";

export interface VerticalBrowseItem {
  id: string;
  name: string;
}

export interface VerticalBrowseListResponse {
  available: boolean;
  verticals: VerticalBrowseItem[];
}

//  wire type — only the fields the vertical-content endpoint is known to return
export interface VerticalContentItem {
  videoIndexId: string;
  name: string;
  filename: string;
  state: string;
  dateModified: string;
  folderId: string | null;
}

// Read model at the Thumbnail boundary. Action callbacks are injected by ThumbnailList.
export interface VerticalThumbnailItem extends VerticalContentItem {
  id: string;
  durationInSeconds: number;
  setShowPlayer: boolean;
  tags?: TagsProps[];
}

export const toVerticalThumbnailItem = (
  item: VerticalContentItem,
): VerticalThumbnailItem => ({
  ...item,
  id: item.videoIndexId,
  durationInSeconds: 0,
  setShowPlayer: false,
});

export interface VerticalContentWireResponse {
  items: VerticalContentItem[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface VerticalContentResponse extends Omit<VerticalContentWireResponse, "items"> {
  items: Array<VerticalContentItem | VerticalThumbnailItem>;
}

export interface VerticalServiceResult<T> {
  status: number;
  data: T;
  response?: { status: number; data: unknown };
}

const BROWSE_BASE = `${API_BASEURL}v1/verticals`;

const normalizeAxiosError = (error: { response?: { status: number; data: unknown } }) =>
  error.response ?? error;

export const listVerticalsForBrowse = async (): Promise<
  VerticalServiceResult<VerticalBrowseListResponse>
> =>
  await axios
    .get<VerticalBrowseListResponse>(BROWSE_BASE, { headers: authHeader() })
    .then((r) => ({ status: r.status, data: r.data }))
    .catch(normalizeAxiosError) as VerticalServiceResult<VerticalBrowseListResponse>;

export const getVerticalContent = async (
  verticalId: string,
  pageIndex: number,
  pageSize: number,
  sortOrder: number,
  sortField: string,
): Promise<VerticalServiceResult<VerticalContentResponse>> => {
  const params = new URLSearchParams({
    p: String(pageIndex),
    s: String(pageSize),
    so: String(sortOrder),
    sf: sortField,
  });
  return await axios
    .get<VerticalContentWireResponse>(`${BROWSE_BASE}/${verticalId}/content?${params.toString()}`, {
      headers: authHeader(),
    })
    .then((r) => ({
      status: r.status,
      data: { ...r.data, items: r.data.items.map(toVerticalThumbnailItem) },
    }))
    .catch(normalizeAxiosError) as VerticalServiceResult<VerticalContentResponse>;
};
